import WhatsappIcon from '@mui/icons-material/WhatsApp'
import CardContent from '@mui/material/CardContent'
import CircularProgress from '@mui/material/CircularProgress'
import Grid2 from '@mui/material/Grid2'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'

import CampaignLink from '@/app/component/atom/campaign-link'
import Tooltip from '@/app/component/atom/tooltip'
import { MIC_ACCESS_ERROR } from '@/app/definitions'
import { selectToken } from '@/app/module/auth/store/selectors'
import SendWhatsappDialog from '@/app/module/common/component/send-whatsapp-dialog'
import { extractWhatsappContentFromPart } from '@/app/module/common/whatsapp/whatsapp-content'
import { getFilesItem } from '@/app/module/files/store/actions'
import { formatCost } from '@/app/module/logs/helpers'
import { getWhatsAppMessageStatus } from '@/app/module/logs/message-status'
import { saveWhatsappFile, sendWhatsappMessage } from '@/app/module/logs/store/actions'
import { selectPersonalizationList } from '@/app/module/logs/store/selectors'
import { SendWhatsappMessagePayload } from '@/app/module/logs/types'
import { isInbound } from '@/app/module/logs/utils'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { dateTime } from '@/app/service/util'
import { RootState } from '@/app/store'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { FileType, FileUploadType } from '@/app/types'
import { WhatsAppLog } from '@/app/types/log'

import { DataCell, RowContainer, RowLoader } from './common'
import ContactIcon from './contact-icon'
import ContactPhoneDisplay from './contact-phone-display'
import WhatsappRowContent from './whatsapp-row-content'

type Props = {
  contactDeleted: boolean
  isLoadingRow: boolean
  part: WhatsAppLog
  partId: string
  sender: string
  successT: string
  videoFiles: string[]
  windowWidth: number

  viewContact: (contactId: number) => void
}

const WhatsappRow: React.FC<Props> = (props: Props) => {
  const { contactDeleted, isLoadingRow, part, viewContact } = props
  const { campId, campaignName, contactId, created } = part
  const dispatch = useAppDispatch()
  const token = useSelector(selectToken)
  const orgId = useSelector(selectOrgId)
  const personalizationList = useSelector((state: RootState) =>
    selectPersonalizationList({ customFields: state.customFields }),
  )
  const [sendWhatsappModalOpen, setSendWhatsappModalOpen] = React.useState(false)

  const handleSendWhatsappMessage = () => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setSendWhatsappModalOpen(true)
  }

  const isInboundType = isInbound(part)
  const contactNumber = getContactNumber(part)
  const messageStatus = getWhatsAppMessageStatus(part)
  const senderId = getWhatsappSenderID(part)
  const messageTime = dateTime(created)

  const sendWhatsappMessageHandler = React.useCallback(
    (messageProps: SendWhatsappMessagePayload) => {
      if (!token || !orgId) {
        return Promise.resolve()
      }

      const { search = '', ...rest } = messageProps

      return dispatch(
        sendWhatsappMessage({
          token,
          orgId,
          ...rest,
          search,
        }),
      )
    },
    [orgId, token, dispatch],
  )

  const getFile = React.useCallback(
    (item: string) => {
      if (!token || !orgId) {
        return Promise.resolve()
      }

      return dispatch(getFilesItem({ orgId, token, name: item }))
    },
    [orgId, token, dispatch],
  )

  const saveFile = React.useCallback(
    (file: FileUploadType) => {
      if (!token || !orgId) {
        return Promise.reject(Error)
      }

      return dispatch(saveWhatsappFile({ orgId, token, item: file })).then((res: FileType | Error) => {
        if (res instanceof Error) {
          throw res
        }
        return res
      })
    },
    [orgId, token, dispatch],
  )

  const handleMicAccessError = React.useCallback(
    () =>
      dispatch(
        createNotification({
          'mic-access': {
            type: 'error',
            message: MIC_ACCESS_ERROR,
          },
        }),
      ),
    [dispatch],
  )

  const messageContent = React.useMemo(() => extractWhatsappContentFromPart(part), [part])

  return (
    <RowContainer>
      {isLoadingRow ? (
        <CardContent>
          <Grid2 container spacing={8}>
            <RowLoader size={{ xs: 12 }}>
              <CircularProgress color="primary" size={50} />
            </RowLoader>
          </Grid2>
        </CardContent>
      ) : (
        <CardContent>
          <Grid2 container>
            <Grid2 size={{ xs: 12, sm: 3, md: 3 }}>
              <ContactPhoneDisplay direction={isInboundType ? 'inbound' : 'outbound'} phoneNumber={contactNumber} />
            </Grid2>
            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <span>{messageStatus}</span>
              </Typography>
              <Typography color="primary" align="center" variant="body1">
                <b>({formatCost(part.charged)})</b>
              </Typography>
            </DataCell>
            <DataCell size={{ xs: 3, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <Tooltip title={'Date and Time'}>
                  <span>&nbsp;{messageTime}</span>
                </Tooltip>
              </Typography>
            </DataCell>
            <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
              <CampaignLink align="center" campaignId={campId} campaignName={campaignName} />
            </DataCell>
            <DataCell size={{ xs: 2, sm: 2, md: 2 }}>
              <Typography color="textSecondary" align="center">
                <Tooltip title={'From/To'}>
                  <span>{getWhatsappSenderID(part)}</span>
                </Tooltip>
              </Typography>
            </DataCell>
            <Grid2 size={{ xs: 2, sm: 1, md: 1 }}>
              {!contactDeleted && !!contactId && (
                <Tooltip title={'Send WhatsApp'}>
                  <IconButton
                    className="whatsapp-send-message-btn"
                    aria-label="Send WhatsApp"
                    onClick={handleSendWhatsappMessage()}
                  >
                    <WhatsappIcon />
                  </IconButton>
                </Tooltip>
              )}
              <ContactIcon contactDeleted={contactDeleted} contactId={contactId} onClick={viewContact} />
            </Grid2>
          </Grid2>
          <Grid2 container>
            <ContentCell size={{ xs: 12, sm: 5, md: 4 }}>
              <WhatsappRowContent waContent={messageContent} />
            </ContentCell>
          </Grid2>
        </CardContent>
      )}
      <SendWhatsappDialog
        contactIds={contactId ? [contactId] : []}
        excludeIds={[]}
        includedGroupIds={[]}
        isAllContacts={false}
        isOpen={sendWhatsappModalOpen}
        loading={isLoadingRow}
        personalizationList={personalizationList}
        recipients={[{ label: contactNumber, value: contactNumber }]}
        search={''}
        senderId={senderId}
        totalRecipients={contactId ? 1 : 0}
        closeHandler={() => setSendWhatsappModalOpen(false)}
        getFile={getFile}
        onMicAccessError={handleMicAccessError}
        saveFile={saveFile}
        sendWhatsappMessage={sendWhatsappMessageHandler}
      />
    </RowContainer>
  )
}

const ContentCell = styled(Grid2)({
  marginBottom: '16px',
})

const getContactNumber = ({ sender, recipient, direction }: WhatsAppLog): string => {
  if (direction === 'ingress') {
    return sender
  }
  return recipient
}

const getWhatsappSenderID = ({ sender, recipient, direction }: WhatsAppLog): string => {
  if (direction === 'ingress') {
    return recipient
  }
  return sender
}

export default WhatsappRow
